/**
 * Number formatting for chart axis ticks.
 *
 * Kept as a dependency-free leaf module so it can be unit tested without pulling in ECharts or the
 * `@/features/Charts` barrel (which imports .vue components).
 */

/** Compact large magnitudes so an axis stays short: 1200 -> "1.2k", 3_400_000 -> "3.4M". */
export function formatCompact(value: number): string {
  const abs = Math.abs(value);
  if (abs >= 1e9) return `${(value / 1e9).toFixed(1).replace(/\.0$/, "")}B`;
  if (abs >= 1e6) return `${(value / 1e6).toFixed(1).replace(/\.0$/, "")}M`;
  if (abs >= 1e3) return `${(value / 1e3).toFixed(1).replace(/\.0$/, "")}k`;
  return String(value);
}

/**
 * Axis ticks for the emission bar charts. Three things it has to get right:
 *
 *  - no scientific notation for everyday magnitudes ("1.2e+5" is unreadable on an axis)
 *  - no float artefacts, because ECharts hands us tick values like 0.30000000000000004
 *  - no padding zeros, so a "-6.00 -3.00 0 3.00" axis reads as "-6 -3 0 3"
 *
 * Falls back to exponential only when a value is too small to render in the digits we allow, which
 * keeps a pathological all-tiny-values axis honest instead of collapsing every tick to "0".
 */
export function formatBarAxisValue(value: number): string {
  // A non-finite tick means the axis extent is already broken; a blank label is quieter on the chart
  // than "NaN" or "InfinityB", and there is nothing useful to render either way.
  if (!Number.isFinite(value)) return "";
  if (value === 0) return "0";

  const abs = Math.abs(value);
  if (abs >= 1e3) return formatCompact(value);

  // Two decimals is plenty at or above 1; below it, keep enough places to show the leading digits.
  const decimals = abs >= 1 ? 2 : Math.min(8, Math.ceil(-Math.log10(abs)) + 1);

  // Trim the fixed-notation string directly rather than round-tripping through `Number(…).toString()`:
  // JS switches its own number-to-string over to exponential below 1e-6, which would put "5e-7" back
  // on the axis — the very notation this function exists to avoid.
  const fixed = value.toFixed(decimals);
  const trimmed = fixed.includes(".") ? fixed.replace(/0+$/, "").replace(/\.$/, "") : fixed;

  // Only now, when the value is too small to survive the digits we allow, fall back to exponential
  // rather than printing a misleading "0".
  return Number(trimmed) === 0 ? value.toExponential(1) : trimmed;
}
